import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useDateFormat, DATE_FORMATS } from '../contexts/DateFormatContext';
import { useOddsFormat } from '../contexts/OddsFormatContext';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { User, Mail, Calendar, Crown, Heart, TrendingUp, BookOpen, ArrowRight, Wallet, DollarSign, Users, Pencil, Check, X, Bell, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { currency: contextCurrency, setCurrency: setContextCurrency } = useCurrency();
  const { dateFormat: contextDateFormat, setDateFormat: setContextDateFormat, formatDate } = useDateFormat();
  const { oddsFormat: contextOddsFormat, setOddsFormat: setContextOddsFormat } = useOddsFormat();
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({ strategies: [], tipster_services: [] });
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [tipsterStats, setTipsterStats] = useState(null);
  const [notifyStrategies, setNotifyStrategies] = useState(false);
  const [notifyServices, setNotifyServices] = useState(false);
  const [notifyAIPredictions, setNotifyAIPredictions] = useState(false);
  const [notifyBlogs, setNotifyBlogs] = useState(false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const [currency, setCurrency] = useState(contextCurrency);
  const [dateFormat, setDateFormat] = useState(contextDateFormat);
  const [oddsFormat, setOddsFormat] = useState(contextOddsFormat);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [editingPaypal, setEditingPaypal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Fetch push notification status
  useEffect(() => {
    const fetchPushStatus = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/push/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPushEnabled(response.data.push_enabled || false);
      } catch (error) {
        console.error('Error fetching push status:', error);
      }
    };
    
    fetchPushStatus();
  }, []);

  useEffect(() => {
    if (user) {
      setNotifyStrategies(user.notify_new_strategies || false);
      setNotifyServices(user.notify_new_services || false);
      setNotifyAIPredictions(user.notify_ai_predictions || false);
      setNotifyBlogs(user.notify_new_blogs || false);
      setCurrency(user.currency || 'EUR');
      setDateFormat(user.date_format || 'DD/MM/YYYY');
      setPaypalEmail(user.paypal_email || '');
      setNewName(user.name || '');
    }
    fetchData();
  }, [user]);

  // Sync local state with context when context changes
  useEffect(() => {
    setCurrency(contextCurrency);
    setDateFormat(contextDateFormat);
    setOddsFormat(contextOddsFormat);
  }, [contextCurrency, contextDateFormat, contextOddsFormat]);

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setSavingName(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.put(`${API}/user/update-name`, 
        { name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Update user in context
        setUser({ ...user, name: newName.trim() });
        // Update in localStorage too
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...storedUser, name: newName.trim() }));
        
        toast.success('Name updated successfully');
        setEditingName(false);
      }
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      // Fetch all data in parallel for faster loading
      const requests = [
        axios.get(`${API}/likes/user`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/statistics`, { headers: { Authorization: `Bearer ${token}` } })
      ];

      // Add tipster-specific requests if user is tipster
      if (user?.subscription_plan === 'tipster') {
        requests.push(
          axios.get(`${API}/tipster/wallet`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/tipster/stats`, { headers: { Authorization: `Bearer ${token}` } })
        );
      }

      const results = await Promise.allSettled(requests);
      
      // Handle favorites
      if (results[0].status === 'fulfilled') {
        setFavorites(results[0].value.data);
      }

      // Handle stats
      if (results[1].status === 'fulfilled') {
        setStats(results[1].value.data);
      } else {
        setStats({ total_bets: 0, win_rate: 0, roi: 0, total_returns: 0 });
      }

      // Handle tipster data
      if (user?.subscription_plan === 'tipster') {
        if (results[2]?.status === 'fulfilled') {
          setWallet(results[2].value.data);
        }
        if (results[3]?.status === 'fulfilled') {
          setTipsterStats(results[3].value.data);
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = async (type) => {
    setUpdatingPreferences(true);
    const token = localStorage.getItem('session_token');
    try {
      let newValue, fieldName;
      
      if (type === 'strategies') {
        newValue = !notifyStrategies;
        fieldName = 'notify_new_strategies';
      } else if (type === 'services') {
        newValue = !notifyServices;
        fieldName = 'notify_new_services';
      } else if (type === 'ai_predictions') {
        newValue = !notifyAIPredictions;
        fieldName = 'notify_ai_predictions';
      } else if (type === 'blogs') {
        newValue = !notifyBlogs;
        fieldName = 'notify_new_blogs';
      }
      
      await axios.put(
        `${API}/auth/notification-preferences`,
        { [fieldName]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      if (type === 'strategies') {
        setNotifyStrategies(newValue);
      } else if (type === 'services') {
        setNotifyServices(newValue);
      } else if (type === 'ai_predictions') {
        setNotifyAIPredictions(newValue);
      } else if (type === 'blogs') {
        setNotifyBlogs(newValue);
      }

      // Update user context
      if (setUser) {
        setUser(prev => ({
          ...prev,
          [fieldName]: newValue
        }));
      }

      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setUpdatingPreferences(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/delete-account`, 
        { confirmation: 'DELETE' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Your account has been deleted.');
      localStorage.removeItem('session_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete account';
      toast.error(msg);
    } finally {
      setDeletingAccount(false);
      setShowDeleteAccountDialog(false);
      setDeleteConfirmText('');
    }
  };

  const getPlanDetails = () => {
    const { formatPrice } = useCurrency();
    const plans = {
      free: {
        name: 'Free Plan',
        color: 'from-gray-500 to-gray-700',
        features: ['10 bets per month', 'Basic reports', 'Community access'],
        priceAmount: 0
      },
      premium: {
        name: 'Premium Plan',
        color: 'from-blue-500 to-blue-700',
        features: ['Unlimited bets', 'Advanced analytics', 'Priority support', 'Export reports'],
        priceAmount: 9.99
      },
      tipster: {
        name: 'Tipster Plan',
        color: 'from-purple-500 to-purple-700',
        features: ['All Premium features', 'List your services', 'Earn from tips', 'Featured profile'],
        priceAmount: 29.99
      }
    };
    return plans[user?.subscription_plan] || plans.free;
  };

  const planDetails = getPlanDetails();

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading profile...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">My Profile</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Profile Info */}
            <div className="space-y-6">
              {/* User Info Card */}
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-white font-bold text-4xl">{user?.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      {editingName ? (
                        <div className="flex items-center justify-center gap-2">
                          <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="max-w-[200px] bg-white/10 border-white/20 text-white text-center"
                            placeholder="Enter your name"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveName();
                              if (e.key === 'Escape') {
                                setEditingName(false);
                                setNewName(user?.name || '');
                              }
                            }}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={handleSaveName}
                            disabled={savingName}
                            className="bg-green-600 hover:bg-green-700 h-9 w-9 p-0"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingName(false);
                              setNewName(user?.name || '');
                            }}
                            className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <h2 className="text-2xl font-bold text-white">{user?.name}</h2>
                          <button
                            onClick={() => setEditingName(true)}
                            className="text-gray-400 hover:text-blue-400 transition-colors p-1"
                            title="Edit name"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-center text-gray-400 text-sm mt-2">
                        <Mail className="w-4 h-4 mr-1" />
                        {user?.email}
                      </div>
                      {user?.created_at && (
                        <div className="flex items-center justify-center text-gray-400 text-sm mt-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          Member since {formatDate(user.created_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Settings Card */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    User Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Currency Selector */}
                  <div>
                    <Label className="text-gray-400">Currency</Label>
                    <Select 
                      value={currency}
                      onValueChange={async (value) => {
                        setCurrency(value);
                        setContextCurrency(value);
                        const token = localStorage.getItem('session_token');
                        try {
                          console.log('Updating currency to:', value);
                          const response = await axios.put(`${API}/user/settings?currency=${value}`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          console.log('Currency update response:', response.data);
                          // Update user object in AuthContext to persist the change
                          if (setUser) {
                            setUser(prev => ({ ...prev, currency: value }));
                            console.log('Updated user context with currency:', value);
                          }
                          toast.success('Currency updated');
                        } catch (error) {
                          console.error('Failed to update currency:', error);
                          toast.error('Failed to update currency');
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="JPY">JPY (¥)</SelectItem>
                        <SelectItem value="AUD">AUD (A$)</SelectItem>
                        <SelectItem value="CAD">CAD (C$)</SelectItem>
                        <SelectItem value="CHF">CHF (CHF)</SelectItem>
                        <SelectItem value="CNY">CNY (¥)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Format Selector */}
                  <div>
                    <Label className="text-gray-400">Date Format</Label>
                    <Select 
                      value={dateFormat}
                      onValueChange={async (value) => {
                        setDateFormat(value);
                        setContextDateFormat(value);
                        const token = localStorage.getItem('session_token');
                        try {
                          await axios.put(`${API}/user/settings?date_format=${value}`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          // Update user object in AuthContext to persist the change
                          if (setUser) {
                            setUser(prev => ({ ...prev, date_format: value }));
                          }
                          toast.success('Date format updated');
                        } catch (error) {
                          toast.error('Failed to update date format');
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DATE_FORMATS).map(([key, format]) => (
                          <SelectItem key={key} value={key}>
                            {format.label} - {format.example}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Odds Format Selector */}
                  <div>
                    <Label className="text-gray-400">Odds Format</Label>
                    <Select 
                      value={oddsFormat}
                      onValueChange={async (value) => {
                        setOddsFormat(value);
                        setContextOddsFormat(value);
                        const token = localStorage.getItem('session_token');
                        try {
                          await axios.put(`${API}/user/settings?odds_format=${value}`, {}, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          // Update user object in AuthContext to persist the change
                          if (setUser) {
                            setUser(prev => ({ ...prev, odds_format: value }));
                          }
                          toast.success('Odds format updated');
                        } catch (error) {
                          console.error('Error updating odds format:', error);
                          toast.error('Failed to update odds format');
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="decimal">Decimal - 2.50</SelectItem>
                        <SelectItem value="fractional">Fractional - 3/2</SelectItem>
                        <SelectItem value="american">American - +150</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* PayPal Email for Tipsters */}
                  {user?.subscription_plan === 'tipster' && (
                    <div className="pt-4 border-t border-white/10">
                      <Label className="text-gray-400 mb-2 block">PayPal Email (for receiving payments)</Label>
                      {editingPaypal ? (
                        <div className="space-y-2">
                          <Input
                            type="email"
                            value={paypalEmail}
                            onChange={(e) => setPaypalEmail(e.target.value)}
                            placeholder="your-paypal@email.com"
                            className="bg-white/5 border-white/10 text-white"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={async () => {
                                const token = localStorage.getItem('session_token');
                                try {
                                  await axios.put(`${API}/user/settings?paypal_email=${encodeURIComponent(paypalEmail)}`, {}, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  toast.success('PayPal email updated');
                                  setEditingPaypal(false);
                                  // Update user context
                                  setUser({ ...user, paypal_email: paypalEmail });
                                } catch (error) {
                                  console.error('Error updating PayPal email:', error);
                                  toast.error('Failed to update PayPal email');
                                }
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Save
                            </Button>
                            <Button
                              onClick={() => {
                                setPaypalEmail(user?.paypal_email || '');
                                setEditingPaypal(false);
                              }}
                              variant="outline"
                              className="border-white/10 text-gray-400"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between bg-white/5 p-3 rounded border border-white/10">
                          <span className="text-white">
                            {paypalEmail || 'Not set'}
                          </span>
                          <Button
                            onClick={() => setEditingPaypal(true)}
                            size="sm"
                            variant="outline"
                            className="border-blue-500/50 text-blue-400"
                          >
                            {paypalEmail ? 'Edit' : 'Add'}
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Enter your PayPal email to receive payments when a withdrawal is requested
                      </p>
                    </div>
                  )}

                  {/* Contact Admin Button */}
                  <div className="pt-2 border-t border-white/10">
                    <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-blue-600 hover:bg-blue-700">
                          <Mail className="w-4 h-4 mr-2" />
                          Contact Admin
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="glass-dark border-white/10">
                        <DialogHeader>
                          <DialogTitle className="text-white">Contact Admin</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-gray-400">Subject</Label>
                            <Input
                              value={contactSubject}
                              onChange={(e) => setContactSubject(e.target.value)}
                              className="bg-white/5 border-white/10 text-white"
                              placeholder="Enter subject"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-400">Message</Label>
                            <Textarea
                              value={contactMessage}
                              onChange={(e) => setContactMessage(e.target.value)}
                              className="bg-white/5 border-white/10 text-white min-h-[150px]"
                              placeholder="Enter your message"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={async () => {
                                if (!contactSubject.trim() || !contactMessage.trim()) {
                                  toast.error('Please fill in all fields');
                                  return;
                                }
                                const token = localStorage.getItem('session_token');
                                try {
                                  await axios.post(`${API}/user/contact-admin`, {
                                    subject: contactSubject,
                                    message: contactMessage
                                  }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Message sent to admin');
                                  setContactSubject('');
                                  setContactMessage('');
                                  setShowContactModal(false);
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to send message');
                                }
                              }}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              Send Message
                            </Button>
                            <Button
                              onClick={() => {
                                setContactSubject('');
                                setContactMessage('');
                                setShowContactModal(false);
                              }}
                              variant="ghost"
                              className="text-gray-400"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>

              {/* Current Plan Card */}
              <Card className={`glass-dark border-white/10 bg-gradient-to-br ${planDetails.color}/10`}>
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Crown className="w-5 h-5 mr-2 text-yellow-400" />
                    {planDetails.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold text-white">
                    <Price amount={planDetails.priceAmount} />/month
                  </div>
                  <ul className="space-y-2">
                    {planDetails.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-300">
                        <ArrowRight className="w-4 h-4 mr-2 text-blue-400 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {user?.subscription_plan !== 'tipster' && (
                    <Button
                      onClick={() => navigate('/subscription')}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Upgrade Plan
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Betting Stats Card */}
              {stats && (
                <Card className="glass-dark border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Your Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Bets</span>
                      <span className="text-white font-bold">{stats.total_bets || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Win Rate</span>
                      <span className="text-green-400 font-bold">{stats.win_rate || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">ROI</span>
                      <span className={`font-bold ${stats.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {stats.roi || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Profit/Loss</span>
                      <span className={`font-bold ${stats.total_returns >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        <Price amount={stats.total_returns || 0} />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Email Notifications Card */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-blue-400" />
                    Email Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">New Betting Strategies</h4>
                        <p className="text-sm text-gray-400">Get notified when new strategies are added</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyStrategies}
                          onChange={() => handleNotificationToggle('strategies')}
                          disabled={updatingPreferences}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">New Tip Services</h4>
                        <p className="text-sm text-gray-400">Get notified when new tipsters join</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyServices}
                          onChange={() => handleNotificationToggle('services')}
                          disabled={updatingPreferences}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">🤖 New AI Predictions</h4>
                        <p className="text-sm text-gray-400">Get notified when new AI predictions are generated</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyAIPredictions}
                          onChange={() => handleNotificationToggle('ai_predictions')}
                          disabled={updatingPreferences}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-medium">📝 New Blog Articles</h4>
                        <p className="text-sm text-gray-400">Get notified when new blog articles are published</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyBlogs}
                          onChange={() => handleNotificationToggle('blogs')}
                          disabled={updatingPreferences}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                  </div>

                  {user?.is_admin && (
                    <p className="text-xs text-gray-500 mt-4">
                      Make sure SMTP settings are configured in admin panel for emails to work.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Push Notifications Card */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center">
                      <Bell className="w-5 h-5 mr-2 text-purple-400" />
                      Push Notifications
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${pushEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {pushEnabled ? 'ON' : 'OFF'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-400 mb-4">
                    Enable push notifications to receive instant alerts on your device, even when you're not on the website.
                  </p>
                  
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                    <div>
                      <h4 className="text-white font-medium">{pushEnabled ? 'Push Notifications Enabled' : 'Enable Push Notifications'}</h4>
                      <p className="text-sm text-gray-400">Get instant alerts for predictions, tips, and more</p>
                    </div>
                    <Button
                      disabled={pushLoading}
                      onClick={async () => {
                        setPushLoading(true);
                        try {
                          const { isPushSupported, requestNotificationPermission, subscribeToPush, unsubscribeFromPush, isSubscribedToPush } = await import('../utils/pushNotifications');
                          
                          if (!isPushSupported()) {
                            toast.error('Push notifications are not supported in this browser');
                            setPushLoading(false);
                            return;
                          }
                          
                          const token = localStorage.getItem('session_token');
                          
                          if (pushEnabled) {
                            // Unsubscribe
                            await unsubscribeFromPush(API.replace('/api', ''), token);
                            setPushEnabled(false);
                            toast.success('Push notifications disabled');
                          } else {
                            // Subscribe
                            const permission = await requestNotificationPermission();
                            if (permission !== 'granted') {
                              toast.error('Please allow notifications in your browser settings');
                              setPushLoading(false);
                              return;
                            }
                            await subscribeToPush(API.replace('/api', ''), token);
                            setPushEnabled(true);
                            toast.success('Push notifications enabled! You will now receive instant alerts.');
                          }
                        } catch (error) {
                          console.error('Push notification error:', error);
                          toast.error('Failed to update push notification settings');
                        } finally {
                          setPushLoading(false);
                        }
                      }}
                      className={pushEnabled ? "bg-red-600 hover:bg-red-700 text-white" : "bg-purple-600 hover:bg-purple-700 text-white"}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {pushLoading ? 'Processing...' : (pushEnabled ? 'Disable' : 'Enable')}
                    </Button>
                    
                    {/* Test Push Button - Admin only */}
                    {pushEnabled && user?.is_admin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('session_token');
                            const response = await fetch(`${API}/push/test-self`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            const data = await response.json();
                            if (data.success) {
                              toast.success(data.message || 'Test notification sent!');
                            } else {
                              // Show detailed error from backend
                              const errorMsg = data.error || data.detail || 'Failed to send test notification';
                              const details = data.details ? `\n${data.details.substring(0, 100)}` : '';
                              toast.error(`${errorMsg}${details}`);
                              console.error('Push test failed:', data);
                            }
                          } catch (error) {
                            console.error('Test push error:', error);
                            toast.error(error.message || 'Network error - check console');
                          }
                        }}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      >
                        Test Push
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    Push notifications work best when you install the app on your device. Look for the "Install" option in your browser.
                  </p>
                </CardContent>
              </Card>

              {/* Delete Account Card */}
              {!user?.is_admin && (
                <Card className="glass-dark border-red-500/20">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Trash2 className="w-5 h-5 mr-2 text-red-400" />
                      Delete Account
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-gray-400">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <Button
                      data-testid="delete-account-btn"
                      onClick={() => setShowDeleteAccountDialog(true)}
                      variant="outline"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete My Account
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Tipster Cards & Favorites */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tipster Cards Row - Earnings & Audience Stats */}
              {user?.subscription_plan === 'tipster' && (wallet || tipsterStats) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tipster Wallet Card */}
                  {wallet && (
                    <Card className="glass-dark border-green-500/30 bg-green-900/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center justify-between">
                          <span className="flex items-center">
                            <Wallet className="w-5 h-5 mr-2 text-green-400" />
                            My Earnings
                          </span>
                          <Button
                            size="sm"
                            onClick={() => navigate('/tipster/wallet')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            View Wallet
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white/5 rounded p-3">
                            <p className="text-gray-400 text-xs mb-1">Total Earnings</p>
                            <p className="text-2xl font-bold text-white">
                              <Price amount={wallet.total_earnings || 0} />
                            </p>
                          </div>
                          <div className="bg-white/5 rounded p-3">
                            <p className="text-gray-400 text-xs mb-1">Available</p>
                            <p className="text-2xl font-bold text-green-400">
                              <Price amount={wallet.available_balance || 0} />
                            </p>
                          </div>
                        </div>
                        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3">
                          <p className="text-yellow-400 text-sm flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Platform fee: {wallet.platform_fee_percentage || 20}%
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            Minimum withdrawal: <Price amount={wallet.minimum_withdrawal || 50} />
                          </p>
                        </div>
                        {wallet.can_withdraw && (
                          <Button
                            onClick={() => navigate('/tipster/wallet')}
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            Withdraw Earnings
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Tipster Stats Card - Followers & Subscribers */}
                  {tipsterStats && (
                    <Card className="glass-dark border-blue-500/30 bg-blue-900/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <Users className="w-5 h-5 mr-2 text-blue-400" />
                          Audience Stats
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white/5 rounded p-3">
                            <p className="text-gray-400 text-xs mb-1">Followers</p>
                            <p className="text-2xl font-bold text-blue-400">{tipsterStats.total_followers || 0}</p>
                            <p className="text-gray-500 text-xs mt-1">Total across all services</p>
                          </div>
                          <div className="bg-white/5 rounded p-3">
                            <p className="text-gray-400 text-xs mb-1">Subscribers</p>
                            <p className="text-2xl font-bold text-purple-400">{tipsterStats.total_subscribers || 0}</p>
                            <p className="text-gray-500 text-xs mt-1">Active paid subscriptions</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-white/5 rounded p-2">
                            <p className="text-xs text-gray-400">Services</p>
                            <p className="text-lg font-bold text-white">{tipsterStats.total_services || 0}</p>
                          </div>
                          <div className="bg-white/5 rounded p-2">
                            <p className="text-xs text-gray-400">Tips</p>
                            <p className="text-lg font-bold text-white">{tipsterStats.total_tips || 0}</p>
                          </div>
                          <div className="bg-white/5 rounded p-2">
                            <p className="text-xs text-gray-400">Purchases</p>
                            <p className="text-lg font-bold text-white">{tipsterStats.total_tip_purchases || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Favorite Strategies */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center">
                      <Heart className="w-5 h-5 mr-2 text-red-400" />
                      Favorite Betting Strategies
                    </CardTitle>
                    <Button
                      onClick={() => navigate('/betting-strategies')}
                      variant="ghost"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      Browse All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {favorites.strategies.length === 0 ? (
                    <div className="text-center py-8">
                      <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">No favorite strategies yet</p>
                      <Button
                        onClick={() => navigate('/betting-strategies')}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Explore Strategies
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {favorites.strategies.map((strategy) => (
                        <div
                          key={strategy.id}
                          onClick={() => navigate(`/betting-strategies/${strategy.id}`)}
                          className="glass p-4 rounded-lg hover:bg-white/10 cursor-pointer transition-all"
                        >
                          {strategy.image_url && (
                            <img
                              src={strategy.image_url}
                              alt={strategy.title}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                          )}
                          <h3 className="text-white font-semibold mb-2">{strategy.title}</h3>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">{strategy.category}</span>
                            <span className="text-xs text-blue-400">{strategy.difficulty}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Favorite Tipster Services */}
              <Card className="glass-dark border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center">
                      <Heart className="w-5 h-5 mr-2 text-red-400" />
                      Favorite Tipster Services
                    </CardTitle>
                    <Button
                      onClick={() => navigate('/tip-services')}
                      variant="ghost"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      Browse All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {favorites.tipster_services.length === 0 ? (
                    <div className="text-center py-8">
                      <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">No favorite tipster services yet</p>
                      <Button
                        onClick={() => navigate('/tip-services')}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Explore Services
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {favorites.tipster_services.map((service) => (
                        <div
                          key={service.id}
                          onClick={() => navigate(`/tip-services/${service.id}`)}
                          className="glass p-4 rounded-lg hover:bg-white/10 cursor-pointer transition-all"
                        >
                          <h3 className="text-white font-semibold mb-2">{service.name}</h3>
                          <p className="text-sm text-gray-400 mb-3 line-clamp-2">{service.description}</p>
                          <div className="flex items-center justify-between">
                            {service.subscription_price > 0 ? (
                              <span className="text-blue-400 font-bold">
                                <Price amount={service.subscription_price} />/month
                              </span>
                            ) : (
                              <span className="text-green-400 font-bold">Free</span>
                            )}
                            <span className="text-xs text-gray-400">{service.sport}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={(open) => {
        setShowDeleteAccountDialog(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <DialogContent className="bg-slate-900 border-red-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action is permanent and cannot be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300">
                Deleting your account will:
              </p>
              <ul className="text-sm text-gray-400 mt-2 space-y-1 list-disc list-inside">
                <li>Remove access to your account</li>
                <li>Cancel any active subscriptions</li>
                <li>You will not be able to log in again</li>
              </ul>
            </div>
            <div>
              <Label className="text-sm text-gray-400 mb-2 block">
                Type <span className="text-red-400 font-bold">DELETE</span> to confirm
              </Label>
              <Input
                data-testid="delete-account-confirm-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setShowDeleteAccountDialog(false); setDeleteConfirmText(''); }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-account-confirm-btn"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {deletingAccount ? 'Deleting...' : 'Delete My Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Profile;
